import React, { useState, useEffect, useRef } from 'react';
import { format, isValid } from 'date-fns';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { cn, safeParseDate } from '@/lib/utils';

export default function DateRangePicker({
  initialSettings = {},
  onApply,
  children,
  className,
  compact = false,
  /** Inclusive: if set, Apply is disabled and an error is shown when the selected range has more than this many days. */
  maxRangeDays,
  side = "bottom",
  ...props
}) {
  const { 
    startDate: initialStartDate, 
    endDate: initialEndDate, 
    maxDate, 
    minDate,
    singleDatePicker = false,
    timePicker = false,
    timePicker24Hour = false,
    timePickerIncrement = 1,
    locale = { format: 'MM/dd/yyyy' } 
  } = initialSettings;
  
  // Use safeParseDate to handle various date formats from localStorage
  const [startDate, setStartDate] = useState(() => safeParseDate(initialStartDate, new Date()));
  const [endDate, setEndDate] = useState(() => safeParseDate(initialEndDate, new Date()));
  
  // Initialize time based on current time and format
  const [selectedTime, setSelectedTime] = useState(() => {
    const now = new Date();
    const hours24 = now.getHours();
    const minutes = now.getMinutes();
    
    if (timePicker24Hour) {
      return { hour: hours24, minute: minutes, period: 'AM' };
    } else {
      // Convert to 12-hour format
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const hours12 = hours24 === 0 ? 12 : (hours24 > 12 ? hours24 - 12 : hours24);
      return { hour: hours12, minute: minutes, period: period };
    }
  });
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    from: startDate,
    to: singleDatePicker ? startDate : endDate,
  });
  const [isRangeComplete, setIsRangeComplete] = useState(true);
  const previousRangeRef = useRef({ from: startDate, to: singleDatePicker ? startDate : endDate });

  // Sync selectedRange with current dates when opening calendar
  // This resets any incomplete selections from previous opening
  useEffect(() => {
    if (isOpen) {
      // When opening, reset to the last applied date range
      const range = { from: startDate, to: endDate };
      setSelectedRange(range);
      previousRangeRef.current = range;
      setIsRangeComplete(true);
    }
  }, [isOpen]);

  const handleSelect = (range) => {
    if (singleDatePicker) {
      // Single date picker mode
      const selectedDate = range?.from || range;
      if (selectedDate) {
        setSelectedRange({ from: selectedDate, to: selectedDate });
        previousRangeRef.current = { from: selectedDate, to: selectedDate };
        setIsRangeComplete(true);
      }
      return;
    }
    
    const hadCompleteRange = isRangeComplete;
    const prevRange = previousRangeRef.current;
    
    if (!range?.from) {
      // No date selected
      setSelectedRange({ from: undefined, to: undefined });
      previousRangeRef.current = { from: undefined, to: undefined };
      setIsRangeComplete(false);
      return;
    }
    
    if (hadCompleteRange && prevRange.from && prevRange.to) {
      // User clicked while we had a complete range - always start fresh
      // The clicked date is the one that's different from our previous range
      let clickedDate = range.from;
      
      // If react-day-picker gave us a complete range, find which date changed
      if (range.to) {
        // Check which date is new (not in our previous range)
        const fromChanged = range.from.getTime() !== prevRange.from.getTime();
        const toChanged = range.to.getTime() !== prevRange.to.getTime();
        
        if (toChanged) {
          // User clicked the 'to' date
          clickedDate = range.to;
        } else if (fromChanged) {
          // User clicked the 'from' date
          clickedDate = range.from;
        }
      }
      
      // Start fresh with just the clicked date
      const newRange = { from: clickedDate, to: undefined };
      setSelectedRange(newRange);
      previousRangeRef.current = newRange;
      setIsRangeComplete(false);
    } else if (range.from && range.to) {
      // Complete range
      setSelectedRange(range);
      previousRangeRef.current = range;
      setIsRangeComplete(true);
    } else {
      // Partial range
      setSelectedRange({ from: range.from, to: undefined });
      previousRangeRef.current = { from: range.from, to: undefined };
      setIsRangeComplete(false);
    }
  };

  const handleApply = () => {
    const dateToUse = singleDatePicker ? selectedRange.from : selectedRange.from;
    const endDateToUse = singleDatePicker ? selectedRange.from : selectedRange.to;

    if (!singleDatePicker && maxRangeDays && dateToUse && endDateToUse) {
      const d = Math.abs(
        Math.round((endDateToUse.getTime() - dateToUse.getTime()) / (1000 * 60 * 60 * 24))
      );
      if (d > maxRangeDays - 1) return;
    }

    if (dateToUse && (singleDatePicker || endDateToUse)) {
      // Combine date with time if timePicker is enabled
      let finalDate = new Date(dateToUse);
      
      if (timePicker) {
        let hours = selectedTime.hour;
        if (!timePicker24Hour && selectedTime.period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (!timePicker24Hour && selectedTime.period === 'AM' && hours === 12) {
          hours = 0;
        }
        finalDate.setHours(hours);
        finalDate.setMinutes(selectedTime.minute);
        finalDate.setSeconds(0);
      }
      
      // Update the actual applied dates
      setStartDate(finalDate);
      if (!singleDatePicker) {
        setEndDate(endDateToUse);
      }
      setIsRangeComplete(true);
      
      // Pass a picker object that matches the expected interface
      const picker = {
        startDate: finalDate,  // Date object with time
        endDate: singleDatePicker ? finalDate : endDateToUse,
        oldEndDate: startDate  // For compatibility
      };
      if (onApply) {
        onApply({}, picker);
      }
      setIsOpen(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      // Use lowercase dd/yyyy for date-fns format
      const formatStr = (locale.format || 'MM/dd/yyyy').replace('DD', 'dd').replace('YYYY', 'yyyy');
      return format(date, formatStr);
    } catch (e) {
      return '';
    }
  };

  const dayDiff =
    !singleDatePicker && maxRangeDays && selectedRange.from && selectedRange.to
      ? Math.abs(
          Math.round(
            (selectedRange.to.getTime() - selectedRange.from.getTime()) / (1000 * 60 * 60 * 24),
          ),
        )
      : 0;
  const maxRangeInvalid =
    !singleDatePicker &&
    Boolean(maxRangeDays) &&
    selectedRange.from &&
    selectedRange.to &&
    dayDiff > maxRangeDays - 1;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {children || (
          <div className={cn("flex gap-2", className)}>
            <Input
              type="text"
              readOnly
              value={formatDate(startDate)}
              className="cursor-pointer"
              placeholder="Start date"
            />
            <Input
              type="text"
              readOnly
              value={formatDate(endDate)}
              className="cursor-pointer rounded-md"
              placeholder="End date"
            />
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent className={cn("w-auto p-0 shadow-lg", props.popoverClassName)} align="start" side={side}>
        <Calendar
          mode={singleDatePicker ? "single" : "range"}
          selected={singleDatePicker ? selectedRange.from : selectedRange}
          onSelect={handleSelect}
          numberOfMonths={singleDatePicker ? 1 : 2}
          disabled={(() => {
            const matchers = [];
            if (minDate) matchers.push({ before: new Date(minDate) });
            if (maxDate) matchers.push({ after: new Date(maxDate) });
            return matchers.length === 0 ? undefined : matchers.length === 1 ? matchers[0] : matchers;
          })()}
          fromYear={2016}
          toYear={new Date().getFullYear() + 10}
          fromMonth={minDate ? new Date(minDate) : new Date(2016, 0, 1)}
          toMonth={maxDate ? new Date(maxDate) : new Date(new Date().getFullYear() + 10, 11, 31)}
          defaultMonth={selectedRange.from || startDate}
          captionLayout="dropdown"
          className={cn("rounded-lg border-0", props.calendarClassName)}
          classNames={props.calendarClassNames}
        />
        
        {/* Footer with Time Picker and Action Buttons */}
        <div
          className={cn(
            'flex items-center justify-between gap-3 border-t bg-muted/30',
            compact ? 'flex-wrap px-2 py-2' : 'flex-wrap p-4'
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center">
            {maxRangeInvalid && !singleDatePicker && maxRangeDays && (
              <p className="shrink-0 text-sm text-destructive">
                {`You can select maximum ${maxRangeDays} days range`}
              </p>
            )}
            {/* Time Picker Section - Left Side (after error when both shown) */}
            {timePicker && (
            <div className={cn("flex flex-col", compact ? "gap-1" : "gap-2")}>
              <div className={cn("font-medium", compact ? "text-[10px]" : "text-sm")}>Select Time</div>
              <div className={cn("flex items-center", compact ? "gap-1" : "gap-2")}>
              <Select 
                value={selectedTime.hour.toString()} 
                onValueChange={(val) => setSelectedTime(prev => ({ ...prev, hour: parseInt(val) }))}
              >
                  <SelectTrigger className={cn(
                    compact ? "w-9 h-7 text-[10px] px-1 [&>svg]:w-2.5 [&>svg]:h-2.5" : "w-20 h-10"
                  )}>
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                  <SelectContent className={cn(compact && "h-[150px] overflow-y-auto")}>
                  {Array.from({ length: timePicker24Hour ? 24 : 12 }, (_, i) => {
                    const hour = timePicker24Hour ? i : (i + 1); // 1-12 for 12-hour format
                    return (
                      <SelectItem key={i} value={hour.toString()}>
                        {hour.toString().padStart(2, '0')}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
                <span className={cn(compact ? "text-xs" : "text-lg")}>:</span>
              
              <Select 
                value={selectedTime.minute.toString()} 
                onValueChange={(val) => setSelectedTime(prev => ({ ...prev, minute: parseInt(val) }))}
              >
                  <SelectTrigger className={cn(
                    compact ? "w-9 h-7 text-[10px] px-1 [&>svg]:w-2.5 [&>svg]:h-2.5" : "w-20 h-10"
                  )}>
                  <SelectValue placeholder="Min" />
                </SelectTrigger>
                  <SelectContent className={cn(compact && "h-[150px] overflow-y-auto")}>
                  {Array.from({ length: 60 / timePickerIncrement }, (_, i) => {
                    const min = i * timePickerIncrement;
                    return (
                      <SelectItem key={i} value={min.toString()}>
                        {min.toString().padStart(2, '0')}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {!timePicker24Hour && (
                <Select 
                  value={selectedTime.period} 
                  onValueChange={(val) => setSelectedTime(prev => ({ ...prev, period: val }))}
                >
                    <SelectTrigger className={cn(
                      compact ? "w-9 h-7 text-[10px] px-1 [&>svg]:w-2.5 [&>svg]:h-2.5" : "w-20 h-10"
                    )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
            )}
          </div>

          {/* Action Buttons - Right Side */}
          <div className={cn('flex shrink-0', compact ? 'gap-2' : 'gap-3')}>
            <Button
              variant="secondary"
              onClick={() => setIsOpen(false)}
              className={cn(
                compact ? 'h-7 px-2 py-1 text-[10px]' : 'py-2',
                'border-foreground bg-foreground text-primary-foreground',
              )}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={
                singleDatePicker
                  ? !selectedRange.from
                  : !selectedRange.from || !selectedRange.to || maxRangeInvalid
              }
              className={cn(compact ? 'h-7 px-2 py-1 text-[10px]' : 'py-2')}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

